import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import LogoContainer from "../../../../../shared/components/LogoContainer";
import InputForm from "../../../../../shared/components/InputForm";
import FormSelect from "../../../../../shared/components/FormSelect";
import AddAnotherList from "../../../../../shared/components/AddAnotherList";
import { createMortgage } from "../../../../../../app/store/slices/mortgageSlice";
import { selectAccounts, fetchAccounts } from "../../../../../../app/store/slices/accountSlice";
import Spinner from "../../../../../shared/components/Spinner";
import { formatAmount } from '../../../../../../utils/formatAmount';

const MortgageSetup = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const accounts = useSelector(selectAccounts);

  const [mortgages, setMortgages] = useState([]);
  const [currentMortgage, setCurrentMortgage] = useState({
    propertyName: "",
    initialAmount: "",
    mortgageStartDate: "",
    annualInterestRate: "",
    monthlyPayment: "",
    monthlyPaymentDueDate: "", // Keep this field for auto-calculated value
    amountRepaid: "",
    paymentAccountId: location.state?.accountId || "",
    // Add schedule fields for monthly payment
    paymentDueDateType: "",
    paymentDueDateDay: "",
    paymentDueDateMonth: ""
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedCanvasId] = useState(
    location.state?.canvasId ||
      JSON.parse(localStorage.getItem("canvasData") || "{}")?._id ||
      ""
  );

  const dueDateTypes = [
    { value: 'First', label: 'First' },
    { value: 'Last', label: 'Last' },
    { value: 'Specific', label: 'Specific' }
  ];

  const monthDays = Array.from({ length: 31 }, (_, i) => ({
    value: (i + 1).toString(),
    label: (i + 1).toString()
  }));

  const months = [
    { value: 'January', label: 'January' },
    { value: 'February', label: 'February' },
    { value: 'March', label: 'March' },
    { value: 'April', label: 'April' },
    { value: 'May', label: 'May' },
    { value: 'June', label: 'June' },
    { value: 'July', label: 'July' },
    { value: 'August', label: 'August' },
    { value: 'September', label: 'September' },
    { value: 'October', label: 'October' },
    { value: 'November', label: 'November' },
    { value: 'December', label: 'December' }
  ];

  const shouldShowDay = () => {
    return currentMortgage.paymentDueDateType === 'Specific';
  };

  const shouldShowMonth = () => {
    return currentMortgage.paymentDueDateType && currentMortgage.paymentDueDateType !== '';
  };

  const getCurrentMonth = () => {
    const today = new Date();
    return months[today.getMonth()].value;
  };

  const calculateNextDueDate = (dueDateType, dueDateMonth, dueDateDay) => {
    if (!dueDateType || !dueDateMonth) {
      return "";
    }

    const monthIndex = months.findIndex(m => m.value === dueDateMonth);
    if (monthIndex === -1) return "";

    const currentYear = new Date().getFullYear();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    let dueDate;
    
    // Calculate the initial due date for this year
    if (dueDateType === 'First') {
      dueDate = new Date(currentYear, monthIndex, 1);
    } else if (dueDateType === 'Last') {
      dueDate = new Date(currentYear, monthIndex + 1, 0); // Last day of the month
    } else if (dueDateType === 'Specific' && dueDateDay) {
      dueDate = new Date(currentYear, monthIndex, parseInt(dueDateDay));
      // Handle invalid dates (e.g., Feb 31st)
      if (dueDate.getDate() !== parseInt(dueDateDay)) {
        dueDate = new Date(currentYear, monthIndex + 1, 0);
      }
    } else {
      return "";
    }
    
    // If the calculated date is in the past, move to next occurrence
    if (dueDate <= today) {
      if (dueDateType === 'First') {
        // Move to 1st of next month
        dueDate = new Date(currentYear, monthIndex + 1, 1);
        // If December, move to January next year
        if (monthIndex === 11) {
          dueDate = new Date(currentYear + 1, 0, 1);
        }
      } else if (dueDateType === 'Last') {
        // Move to last day of next month
        dueDate = new Date(currentYear, monthIndex + 2, 0);
        // If December, move to January next year
        if (monthIndex === 11) {
          dueDate = new Date(currentYear + 1, 1, 0);
        }
      } else if (dueDateType === 'Specific') {
        dueDate = new Date(currentYear, monthIndex + 1, parseInt(dueDateDay));
        if (dueDate.getDate() !== parseInt(dueDateDay)) {
          dueDate = new Date(currentYear, monthIndex + 2, 0);
        }
      }
    }
    
    return dueDate.toISOString().split('T')[0];
  };

  const getSchedulePreviewText = (dueDateType, dueDateMonth, dueDateDay) => {
    if (!dueDateType || !dueDateMonth) return '';
    
    const calculatedDate = calculateNextDueDate(dueDateType, dueDateMonth, dueDateDay);
    
    if (!calculatedDate) return '';
    
    // Format date as DD/MM/YYYY
    const dateObj = new Date(calculatedDate);
    const formattedDate = dateObj.toLocaleDateString('en-GB');
    
    if (dueDateType === 'First') {
      return `First day of every month (Next: ${formattedDate})`;
    } else if (dueDateType === 'Last') {
      return `Last day of every month (Next: ${formattedDate})`;
    } else if (dueDateType === 'Specific') {
      if (!dueDateDay) return '';
      const dayWithSuffix = getDayWithOrdinalSuffix(dueDateDay);
      return `Every ${dayWithSuffix} of the month (Next: ${formattedDate})`;
    }

    return '';
  };

  // Helper function for ordinal suffixes
  const getDayWithOrdinalSuffix = (day) => {
    const dayNum = parseInt(day);
    if (dayNum >= 11 && dayNum <= 13) {
      return `${dayNum}th`;
    }
    switch (dayNum % 10) {
      case 1: return `${dayNum}st`;
      case 2: return `${dayNum}nd`;
      case 3: return `${dayNum}rd`;
      default: return `${dayNum}th`;
    }
  };

  // Auto-calculate and set due date when schedule changes
  useEffect(() => {
    if (currentMortgage.paymentDueDateType && currentMortgage.paymentDueDateMonth) {
      const calculatedDate = calculateNextDueDate(currentMortgage.paymentDueDateType, currentMortgage.paymentDueDateMonth, currentMortgage.paymentDueDateDay);
      if (calculatedDate && calculatedDate !== currentMortgage.monthlyPaymentDueDate) {
        setCurrentMortgage(prev => ({ ...prev, monthlyPaymentDueDate: calculatedDate }));
      }
    }
  }, [currentMortgage.paymentDueDateType, currentMortgage.paymentDueDateMonth, currentMortgage.paymentDueDateDay]);

  // Set default month to current month when type is selected
  useEffect(() => {
    if (currentMortgage.paymentDueDateType && !currentMortgage.paymentDueDateMonth) {
      setCurrentMortgage(prev => ({ ...prev, paymentDueDateMonth: getCurrentMonth() }));
    }
  }, [currentMortgage.paymentDueDateType]);

  useEffect(() => {
    if (selectedCanvasId) {
      dispatch(fetchAccounts({ canvasId: selectedCanvasId, limit:1000 }));
    }
  }, [dispatch, selectedCanvasId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCurrentMortgage({ ...currentMortgage, [name]: value });
  };

  const handleAddMortgage = async () => {
    if (
      currentMortgage.propertyName &&
      currentMortgage.initialAmount &&
      currentMortgage.mortgageStartDate &&
      currentMortgage.annualInterestRate &&
      currentMortgage.monthlyPayment &&
      currentMortgage.monthlyPaymentDueDate &&
      currentMortgage.paymentAccountId &&
      selectedCanvasId
    ) {
      try {
        await dispatch(
          createMortgage({
            canvasId: selectedCanvasId,
            mortgageData: {
              propertyName: currentMortgage.propertyName,
              initialAmount: Number(currentMortgage.initialAmount),
              mortgageStartDate: currentMortgage.mortgageStartDate,
              annualInterestRate: Number(currentMortgage.annualInterestRate),
              monthlyPayment: Number(currentMortgage.monthlyPayment),
              monthlyPaymentDueDate: currentMortgage.monthlyPaymentDueDate,
              amountRepaid: Number(currentMortgage.amountRepaid) || 0,
              paymentAccountId: currentMortgage.paymentAccountId,
              // Include schedule metadata
              paymentDueDateType: currentMortgage.paymentDueDateType,
              paymentDueDateDay: currentMortgage.paymentDueDateDay,
              paymentDueDateMonth: currentMortgage.paymentDueDateMonth
            },
          })
        ).unwrap();
        setMortgages([...mortgages, currentMortgage]);
        setCurrentMortgage({
          propertyName: "",
          initialAmount: "",
          mortgageStartDate: "",
          annualInterestRate: "",
          monthlyPayment: "",
          monthlyPaymentDueDate: "",
          amountRepaid: "",
          paymentAccountId: location.state?.accountId ||  "",
          paymentDueDateType: "",
          paymentDueDateDay: "",
          paymentDueDateMonth: ""
        });
      } catch (err) {
        console.error("Failed to create mortgage:", err);
      }
    }
  };

  const handleContinue = async () => {
    setIsProcessing(true);
    try {
      if (
        currentMortgage.propertyName &&
        currentMortgage.initialAmount &&
        currentMortgage.mortgageStartDate &&
        currentMortgage.annualInterestRate &&
        currentMortgage.monthlyPayment &&
        currentMortgage.monthlyPaymentDueDate &&
        currentMortgage.paymentAccountId &&
        selectedCanvasId
      ) {
        await dispatch(
          createMortgage({
            canvasId: selectedCanvasId,
            mortgageData: {
              propertyName: currentMortgage.propertyName,
              initialAmount: Number(currentMortgage.initialAmount),
              mortgageStartDate: currentMortgage.mortgageStartDate,
              annualInterestRate: Number(currentMortgage.annualInterestRate),
              monthlyPayment: Number(currentMortgage.monthlyPayment),
              monthlyPaymentDueDate: currentMortgage.monthlyPaymentDueDate,
              amountRepaid: Number(currentMortgage.amountRepaid) || 0,
              paymentAccountId: currentMortgage.paymentAccountId,
              // Include schedule metadata
              paymentDueDateType: currentMortgage.paymentDueDateType,
              paymentDueDateDay: currentMortgage.paymentDueDateDay,
              paymentDueDateMonth: currentMortgage.paymentDueDateMonth
            },
          })
        ).unwrap();
        setMortgages((prev) => [...prev, currentMortgage]);
        setCurrentMortgage({
          propertyName: "",
          initialAmount: "",
          mortgageStartDate: "",
          annualInterestRate: "",
          monthlyPayment: "",
          monthlyPaymentDueDate: "",
          amountRepaid: "",
          paymentAccountId: location.state?.accountId || "",
          paymentDueDateType: "",
          paymentDueDateDay: "",
          paymentDueDateMonth: ""
        });
      }
      navigate("/investment", {
        state: { accountId: currentMortgage.paymentAccountId, canvasId: selectedCanvasId },
      });
    } catch (err) {
      console.error("Failed to create mortgage:", err);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <LogoContainer />
      <div className="card-setup">
        <h2 className="txt-page-heading">Enter your Mortgages</h2>
        <p className="p-tag-below-heading text-center">
          Enter your mortgage details and repayment progress
        </p>
        <div className="w-full flex flex-col items-center">
          <div className="space-y-4 w-full max-w-lg mx-auto">
            <InputForm
              label="Property Name"
              type="text"
              id="propertyName"
              name="propertyName"
              value={currentMortgage.propertyName}
              onChange={handleInputChange}
              placeholder="eg Family Home"
            />
            <InputForm
              label="Initial Amount"
              type="number"
              id="initialAmount"
              name="initialAmount"
              value={currentMortgage.initialAmount}
              onChange={handleInputChange}
              placeholder="Initial amount"
              min="0"
            />
            <InputForm
              label="Mortgage Start Date"
              type="date"
              id="mortgageStartDate"
              name="mortgageStartDate"
              value={currentMortgage.mortgageStartDate}
              onChange={handleInputChange}
            />
            <InputForm
              label="Annual Interest Rate (%)"
              type="number"
              id="annualInterestRate"
              name="annualInterestRate"
              value={currentMortgage.annualInterestRate}
              onChange={handleInputChange}
              placeholder="Interest rate (%)"
              min="0"
              step="0.01"
            />
            <InputForm
              label="Monthly Payment"
              type="number"
              id="monthlyPayment"
              name="monthlyPayment"
              value={currentMortgage.monthlyPayment}
              onChange={handleInputChange}
              placeholder="Monthly payment"
              min="0"
              step="0.01"
            />

            {/* Payment Schedule Section */}
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Payment Due Type
                  </label>
                  <select
                    value={currentMortgage.paymentDueDateType}
                    onChange={(e) => setCurrentMortgage({ ...currentMortgage, paymentDueDateType: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                  >
                    <option value="">Select Type</option>
                    {dueDateTypes.map(type => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                </div>

                {shouldShowMonth() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Starting Month
                    </label>
                    <select
                      value={currentMortgage.paymentDueDateMonth || ''}
                      onChange={(e) => setCurrentMortgage({ ...currentMortgage, paymentDueDateMonth: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                    >
                      <option value="">Select Month</option>
                      {months.map(month => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {shouldShowDay() && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Day
                    </label>
                    <select
                      value={currentMortgage.paymentDueDateDay || ''}
                      onChange={(e) => setCurrentMortgage({ ...currentMortgage, paymentDueDateDay: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-btn-primary focus:border-transparent"
                    >
                      <option value="">Select Day</option>
                      {monthDays.map(day => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {(currentMortgage.paymentDueDateType && currentMortgage.paymentDueDateMonth) && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    <span className="font-medium">Payment Schedule Preview:</span>
                    {getSchedulePreviewText(currentMortgage.paymentDueDateType, currentMortgage.paymentDueDateMonth, currentMortgage.paymentDueDateDay)}
                  </p>
                </div>
              )}
            </div>

            {/* Remove the manual date input - now auto-calculated */}
            {/* <InputForm
              label="Monthly Payment Due Date"
              type="date"
              id="monthlyPaymentDueDate"
              name="monthlyPaymentDueDate"
              value={currentMortgage.monthlyPaymentDueDate}
              onChange={handleInputChange}
            /> */}

            <InputForm
              label="Amount Repaid"
              type="number"
              id="amountRepaid"
              name="amountRepaid"
              value={currentMortgage.amountRepaid}
              onChange={handleInputChange}
              placeholder="Amount repaid"
              min="0"
              step="0.01"
            />
            <FormSelect
              label="Payment Account"
              id="paymentAccountId"
              name="paymentAccountId"
              value={currentMortgage.paymentAccountId}
              onChange={handleInputChange}
              options={[
                { value: "", label: "Select Account" },
                ...(accounts || []).map((acc) => ({
                  value: acc._id,
                  label: `${acc.name} (${acc.currency})`,
                })),
              ]}
            />
          </div>
          {/* Centered action buttons */}
          <div className="mt-8 flex flex-row justify-center gap-6 w-full max-w-lg">
            <button
              type="button"
              className="btn-back flex-1"
              onClick={() => {
                setCurrentMortgage({
                  propertyName: "",
                  initialAmount: "",
                  mortgageStartDate: "",
                  annualInterestRate: "",
                  monthlyPayment: "",
                  monthlyPaymentDueDate: "",
                  amountRepaid: "",
                  paymentAccountId: location.state?.accountId || "",
                  paymentDueDateType: "",
                  paymentDueDateDay: "",
                  paymentDueDateMonth: ""
                });
                navigate("/investment", {
                  state: { accountId: currentMortgage.paymentAccountId, canvasId: selectedCanvasId },
                });
              }}
            >
              Skip
            </button>
            <button
              type="button"
              className="btn-continue flex-1"
              onClick={handleAddMortgage}
            >
              Add Another Mortgage
            </button>
          </div>
        </div>
        <AddAnotherList
          title="Your Mortgages"
          items={mortgages}
          renderItem={(mortgage) => (
            <div>
              <div className="font-medium">{mortgage.propertyName}</div>
              <div className="text-sm text-gray-500">{formatAmount(mortgage.initialAmount)}</div>
              {mortgage.paymentDueDateType && (
                <div className="text-xs text-blue-600">
                  Payment Due: {mortgage.paymentDueDateType === 'First' ? '1st' : mortgage.paymentDueDateType === 'Last' ? 'Last' : `${mortgage.paymentDueDateDay}`} of each month
                  {mortgage.monthlyPaymentDueDate && ` (Next: ${new Date(mortgage.monthlyPaymentDueDate).toLocaleDateString()})`}
                </div>
              )}
            </div>
          )}
        />
        <div className="flex flex-row justify-between items-center w-full mt-12">
          <button
            className="btn-back"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button
            className="btn-continue"
            onClick={handleContinue}
          >
            {isProcessing ? (
              <Spinner text="Processing..." />
            ) : (
              "Save & Continue"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MortgageSetup;